import React from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';

const formatPrice = (price) =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(price);

/**
 * ColombiaProductCard - Card styled exactly like the Sabor Burger House reference image:
 * - White/cream background
 * - Product image on the left
 * - Name in dark blue bold
 * - Price in red (discounted) or blue
 * - Soccer ball watermark on the right
 * - Flag brush stroke on the bottom right corner
 */
export const ColombiaProductCard = ({ product, theme, onSelect }) => {
    const hasImage = !!product.image_url;
    const hasDiscount = !!(product.discounted_price && Number(product.discounted_price) > 0 && Number(product.discounted_price) < Number(product.price));
    const finalPrice = hasDiscount ? product.discounted_price : product.price;

    return (
        <Box
            onClick={() => onSelect && onSelect(product)}
            sx={{
                display: 'flex',
                flexDirection: 'row',
                bgcolor: '#FFFFFF',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 3px 16px rgba(0,0,0,0.12)',
                border: `1px solid rgba(0,56,147,0.1)`,
                cursor: onSelect ? 'pointer' : 'default',
                position: 'relative',
                minHeight: '120px',
                transition: 'all 0.25s ease',
                '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: `0 8px 28px rgba(0,56,147,0.25)`,
                    border: `1px solid ${theme.yellow}`,
                },
            }}
        >
            {/* Left accent border = tricolor */}
            <Box sx={{
                position: 'absolute', top: 0, left: 0, width: '6px', height: '100%',
                background: theme.flagGradientVertical,
            }} />

            {/* Product image */}
            {hasImage && (
                <Box sx={{
                    width: { xs: '110px', sm: '140px' },
                    minWidth: { xs: '110px', sm: '140px' },
                    overflow: 'hidden',
                    ml: '6px',  // offset for left border
                    flexShrink: 0,
                }}>
                    <Box
                        component="img"
                        src={product.image_url}
                        alt={product.name}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                </Box>
            )}

            {/* Content area */}
            <Box sx={{
                flex: 1,
                pl: hasImage ? 1.5 : 2,
                pr: 1,
                py: 1.5,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
                overflow: 'hidden',
            }}>
                {/* Soccer ball watermark */}
                <Box sx={{
                    position: 'absolute', right: -20, top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: 0.06, pointerEvents: 'none',
                }}>
                    <SportsSoccerIcon sx={{ fontSize: 100, color: '#000' }} />
                </Box>

                {/* Flag brush corner */}
                <Box sx={{
                    position: 'absolute', bottom: 0, right: 0,
                    width: '70px', height: '50px',
                    background: theme.flagGradient,
                    opacity: 0.25,
                    clipPath: 'polygon(100% 0, 100% 100%, 0% 100%)',
                    pointerEvents: 'none',
                }} />

                {/* Top: labels */}
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                    {product.is_promo && (
                        <Chip label="PROMO" size="small" sx={{
                            bgcolor: theme.red, color: '#FFF', fontWeight: 900, fontSize: '0.6rem', height: '20px', borderRadius: '4px',
                        }} />
                    )}
                    {product.is_new && (
                        <Chip label="NUEVO" size="small" sx={{
                            bgcolor: theme.yellow, color: theme.blue, fontWeight: 900, fontSize: '0.6rem', height: '20px', borderRadius: '4px',
                        }} />
                    )}
                </Box>

                {/* Product name */}
                <Typography sx={{
                    fontWeight: 900,
                    fontSize: { xs: '0.95rem', sm: '1.05rem' },
                    color: theme.blue,
                    textTransform: 'uppercase',
                    lineHeight: 1.2,
                    letterSpacing: '0.5px',
                    mb: 0.5,
                    position: 'relative', zIndex: 1,
                }}>
                    {product.name}
                </Typography>

                {/* Prices */}
                <Box sx={{ mb: 0.5, position: 'relative', zIndex: 1 }}>
                    {hasDiscount && (
                        <Typography sx={{
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            color: '#999',
                            textDecoration: 'line-through',
                            textDecorationColor: theme.red,
                            lineHeight: 1,
                        }}>
                            {formatPrice(product.price)}
                        </Typography>
                    )}
                    <Typography sx={{
                        fontWeight: 900,
                        fontSize: { xs: '1.1rem', sm: '1.3rem' },
                        color: theme.red,
                        lineHeight: 1.1,
                    }}>
                        {formatPrice(finalPrice)}
                    </Typography>
                    {/* Yellow underline accent */}
                    <Box sx={{
                        width: '40px', height: '3px',
                        bgcolor: theme.yellow, borderRadius: '2px', mt: 0.3,
                    }} />
                </Box>

                {/* Description */}
                {product.description && (
                    <Typography sx={{
                        fontSize: '0.75rem',
                        color: '#666',
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        overflow: 'hidden',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        position: 'relative', zIndex: 1,
                    }}>
                        {product.description}
                    </Typography>
                )}

                {/* Add button */}
                {onSelect && (
                    <Box sx={{ position: 'absolute', bottom: 8, right: 8, zIndex: 2 }}>
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onSelect(product); }}
                            sx={{
                                bgcolor: theme.yellow,
                                color: theme.blue,
                                p: 0.5,
                                '&:hover': { bgcolor: theme.blue, color: theme.yellow },
                                boxShadow: `0 4px 12px rgba(252,209,22,0.5)`,
                                transition: 'all 0.2s',
                            }}
                        >
                            <AddCircleOutlineIcon sx={{ fontSize: 22 }} />
                        </IconButton>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
